import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Code, Clock, Trash2, FolderOpen, Folder, FolderPlus, Loader2, CheckCircle, AlertCircle, Rocket, Pause, Sparkles, Github, Gitlab, ExternalLink, Search, X, MoreVertical, Copy, ChevronDown, ChevronRight, ArrowLeft, Pencil, FolderInput } from 'lucide-react';

import axios from 'axios';
import toast from 'react-hot-toast';

import { editorAPI } from '@/lib/editor/api';
import { TEMPLATE_ID } from '@/lib/templates';
import { useAuth } from '@/contexts/AuthContext';
import { useEmbedSubscriptionGate } from '@/embed/useEmbedSubscriptionGate';
import { EmbedSubscriptionBlockedModal } from '@/embed/EmbedSubscriptionBlockedModal';
import { usePermissions } from '@/hooks/usePermissions';
import { getActiveWorkspaceId } from '@/stores/workspace-store';
import { useProjectStore } from '@/stores/projectStore';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PauseContainerModal } from '@/components/dev-containers/PauseContainerModal';
import { GitHubImportModal } from '@/components/github';
import { GitLabImportModal } from '@/components/gitlab';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Project {
  id: string;
  name: string;
  description?: string;
  templateName?: string;
  createdAt: string;
  updatedAt: string;
  mode?: 'lite' | 'power';
  devContainerId?: string;
  workspaceId: string;
  thumbnailUrl?: string;
  latestPublishUrl?: string | null;
  folderId?: string | null;
}

interface ProjectFolderItem {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface DevContainerStatus {
  hasDevContainer: boolean;
  status: string | null;
  devContainerUrl: string | null;
  devContainerId?: string;
  subdomain?: string;
}

export default function EditorListFull() {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { canCreateProject, canDeleteProject, canEditProject } = usePermissions();
  const { projectListVersion } = useProjectStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [devContainerStatuses, setDevContainerStatuses] = useState<Record<string, DevContainerStatus>>({});
  const [lastPollTime, setLastPollTime] = useState<number | null>(null);
  const [pausingContainers, setPausingContainers] = useState<Record<string, boolean>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [generatingName, setGeneratingName] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseModalData, setPauseModalData] = useState<{
    devContainerId: string;
    taskCount: number;
    mode: 'confirm' | 'active-tasks' | 'retry';
  } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGitlabImportModal, setShowGitlabImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Folder state
  const [folders, setFolders] = useState<ProjectFolderItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderName, setCurrentFolderName] = useState<string | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState<ProjectFolderItem | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<ProjectFolderItem | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [projectToMove, setProjectToMove] = useState<Project | null>(null);
  const [movingProject, setMovingProject] = useState(false);

  // Server-side search: projects already filtered by backend
  const projectsList = projects ?? [];
  const filteredProjects = projectsList;

  // Check if manual pause feature is enabled
  const enableManualPause = import.meta.env.VITE_ENABLE_MANUAL_CONTAINER_PAUSE === 'true';

  // Auto-open import modal if redirected from GitHub OAuth
  useEffect(() => {
    if (searchParams.get('openImport') === 'true') {
      setShowImportModal(true);
      // Clear the query param to avoid reopening on refresh
      searchParams.delete('openImport');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-open GitLab import modal if redirected from GitLab OAuth
  useEffect(() => {
    if (searchParams.get('openGitlabImport') === 'true') {
      setShowGitlabImportModal(true);
      searchParams.delete('openGitlabImport');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-open create project modal when navigated from onboarding checklist
  useEffect(() => {
    if (searchParams.get('openCreateModal') === 'true') {
      handleOpenCreateModal();
      searchParams.delete('openCreateModal');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user) {
      loadProjects();
      loadFolders();
    }
  }, [user]);

  // Refresh project list when projectListVersion changes (e.g., after rename)
  useEffect(() => {
    // Skip initial mount (version 0), only refetch on increments
    if (projectListVersion > 0 && user) {
      loadProjects();
    }
  }, [projectListVersion]);

  // Reload projects when folder changes
  useEffect(() => {
    if (user) {
      setSearchQuery('');
      setDebouncedSearch('');
      setLoading(true);
      loadProjects();
    }
  }, [currentFolderId]);

  // Delta sync polling - single call for all projects instead of N calls
  useEffect(() => {
    // Only poll if there are Power mode projects
    const hasPowerProjects = projects.some(p => p.mode === 'power' && p.devContainerId);
    if (!hasPowerProjects) return;

    const pollInterval = setInterval(async () => {
      await pollDevContainerStatusChanges();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [projects, lastPollTime]);

  // Debounce search query (300ms)
  // Show spinner immediately when user types, hide when API completes
  useEffect(() => {
    const trimmed = searchQuery.trim();

    // Show spinner immediately when user starts typing
    if (trimmed) {
      setSearchLoading(true);
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearch(trimmed);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Trigger API search when debounced search changes
  useEffect(() => {
    if (!user) return;
    // Skip initial load (handled by user useEffect)
    if (debouncedSearch === '' && loading) return;
    loadProjects(debouncedSearch || undefined);
  }, [debouncedSearch]);

  const loadFolders = async () => {
    try {
      const data = await editorAPI.listFolders();
      setFolders(data);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const loadProjects = async (search?: string) => {
    try {
      // Always show loading when actively searching (search param provided)
      if (search !== undefined) {
        setSearchLoading(true);
      }
      const filters: { search?: string; folderId?: string | null } = {};
      if (search) {
        filters.search = search;
        // When searching inside a folder, scope to that folder
        if (currentFolderId) {
          filters.folderId = currentFolderId;
        }
      } else {
        // When not searching, filter by current folder (null = root)
        filters.folderId = currentFolderId;
      }
      const data = await editorAPI.listProjects(filters);
      setProjects(data);

      // Fetch initial statuses for all Power mode projects (no since param = get all)
      const hasPowerProjects = data.some((p: Project) => p.mode === 'power' && p.devContainerId);
      if (hasPowerProjects) {
        // Pass data directly since setProjects is async and state not updated yet
        await pollDevContainerStatusChanges(true, data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  /**
   * Poll for dev container status changes using delta sync
   * @param initial - If true, fetches all statuses (no since param)
   * @param projectList - Optional projects list (used on initial load when state not yet updated)
   */
  const pollDevContainerStatusChanges = async (initial = false, projectList?: Project[]) => {
    try {
      const since = initial ? undefined : (lastPollTime ?? undefined);
      const { changes, serverTime } = await editorAPI.getDevContainerStatusChanges(since);

      // Update serverTime for next poll
      setLastPollTime(serverTime);

      // Use provided list or current state
      const projectsToMatch = projectList || projects;

      // Apply changes to state (idempotent - duplicates just overwrite with same value)
      // Match by devContainerId since frontend projects already have this field
      if (changes.length > 0) {
        setDevContainerStatuses(prev => {
          const updated = { ...prev };
          for (const change of changes) {
            // Find project by devContainerId
            const project = projectsToMatch.find(p => p.devContainerId === change.devContainerId);
            if (project) {
              // Use response directly - matches individual API shape
              updated[project.id] = change;
            }
          }
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to poll dev container status changes:', error);
    }
  };

  // Keep legacy function for pause action refresh (single project)
  const checkDevContainerStatus = async (projectId: string) => {
    try {
      const status = await editorAPI.getProjectDevContainerStatus(projectId);
      setDevContainerStatuses(prev => ({ ...prev, [projectId]: status }));
    } catch (error) {
      console.error('Failed to check dev container status:', error);
    }
  };

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      await editorAPI.deleteProject(projectToDelete.id);
      setShowDeleteModal(false);
      setProjectToDelete(null);
      await loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Folder handlers
  const handleOpenFolder = (folder: ProjectFolderItem) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  };

  const handleBackToRoot = () => {
    setCurrentFolderId(null);
    setCurrentFolderName(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      await editorAPI.createFolder(newFolderName.trim());
      setShowCreateFolderModal(false);
      setNewFolderName('');
      await loadFolders();
    } catch (error: any) {
      console.error('Failed to create folder:', error);
      toast.error(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameFolder = async () => {
    if (!folderToRename || !renameFolderName.trim()) return;
    try {
      await editorAPI.renameFolder(folderToRename.id, renameFolderName.trim());
      setShowRenameFolderModal(false);
      setFolderToRename(null);
      await loadFolders();
      // Update current folder name if we renamed the folder we're in
      if (currentFolderId === folderToRename.id) {
        setCurrentFolderName(renameFolderName.trim());
      }
    } catch (error: any) {
      console.error('Failed to rename folder:', error);
      toast.error(error.response?.data?.message || 'Failed to rename folder');
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    setDeletingFolder(true);
    try {
      await editorAPI.deleteFolder(folderToDelete.id);
      setShowDeleteFolderModal(false);
      setFolderToDelete(null);
      await loadFolders();
      // If we deleted the folder we were in, go back to root
      if (currentFolderId === folderToDelete.id) {
        handleBackToRoot();
      }
    } catch (error: any) {
      console.error('Failed to delete folder:', error);
      const message = error.response?.data?.message || 'Failed to delete folder';
      toast.error(message);
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleMoveProject = async (targetFolderId: string | null) => {
    if (!projectToMove) return;
    setMovingProject(true);
    try {
      await editorAPI.moveProjectToFolder(projectToMove.id, targetFolderId);
      setShowMoveModal(false);
      setProjectToMove(null);
      await loadProjects();
      toast.success('Project moved');
    } catch (error) {
      console.error('Failed to move project:', error);
      toast.error('Failed to move project');
    } finally {
      setMovingProject(false);
    }
  };

  // Handler for pause button click - ALWAYS shows confirmation modal
  const handlePauseClick = async (devContainerId: string) => {
    if (!devContainerId) return;

    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      // Check for active tasks to determine modal content
      const { data } = await axios.get(
        `${API_URL}/api/claude-agent/tasks/active/dev-container/${devContainerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Always show modal - mode determines content
      const mode = data.hasActiveTasks ? 'active-tasks' : 'confirm';
      setPauseModalData({ devContainerId, taskCount: data.count, mode });
      setShowPauseModal(true);
    } catch (error) {
      console.error('Failed to check active tasks:', error);
      // Fallback: show simple confirmation modal
      setPauseModalData({ devContainerId, taskCount: 0, mode: 'confirm' });
      setShowPauseModal(true);
    }
  };

  // Actual pause handler with force option
  const handlePauseContainer = async (devContainerId: string, force: boolean = false) => {
    if (!devContainerId) return;

    setPausingContainers(prev => ({ ...prev, [devContainerId]: true }));

    try {
      const token = await getIdToken();
      if (!token) {
        console.error('No authentication token');
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(
        `${API_URL}/api/dev-containers/workspaces/${devContainerId}/pause${force ? '?force=true' : ''}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Reload project status after pausing
      await checkDevContainerStatus(projects.find(p => p.devContainerId === devContainerId)?.id || '');
      toast.success('Container paused successfully');
      setShowPauseModal(false);
      setPauseModalData(null);
    } catch (error: any) {
      console.error('Failed to pause container:', error);
      const message = error.response?.data?.message || 'Failed to pause container. Please try again.';

      // Check if it's a backup failure - show retry modal instead of toast
      if (message.includes('backup failed') && !force) {
        // Show retry modal for trying again with force
        setPauseModalData({ devContainerId, taskCount: 0, mode: 'retry' });
        // Keep modal open, don't show toast
        return;
      }

      toast.error(message);
      setShowPauseModal(false);
      setPauseModalData(null);
    } finally {
      setPausingContainers(prev => ({ ...prev, [devContainerId]: false }));
    }
  };

  // Modal handler for pause confirmation
  const handlePauseConfirm = async (force: boolean) => {
    if (pauseModalData) {
      await handlePauseContainer(pauseModalData.devContainerId, force);
    }
  };

  const handleOpenCreateModal = async () => {
    setProjectName('');
    setShowCreateModal(true);
    await generateProjectName();
  };

  const generateProjectName = async () => {
    try {
      setGeneratingName(true);
      const response = await editorAPI.suggestProjectName();
      setProjectName(response.suggestedName);
    } catch (error) {
      console.error('Failed to generate project name:', error);
    } finally {
      setGeneratingName(false);
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    setCreating(true);
    try {
      const project = await editorAPI.cloneTemplate(TEMPLATE_ID.BASE, {
        projectName,
        mode: 'power',
        folderId: currentFolderId || undefined,
      });

      // Navigate to the newly created project
      navigate(`/dashboard/editor/${project.id}`);
      setShowCreateModal(false);
    } catch (error: any) {
      console.error('Failed to create project:', error);
      // Show specific error message from backend (e.g., slug collision)
      const errorMessage = error.response?.data?.message || 'Failed to create project. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const getFolderName = (folderId: string | null | undefined): string | null => {
    if (!folderId) return null;
    return folders.find(f => f.id === folderId)?.name || null;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const { isBlocked: isEmbedBlocked } = useEmbedSubscriptionGate();

  if (isEmbedBlocked) {
    return <EmbedSubscriptionBlockedModal />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <div className="mb-5 sm:mb-10">
          {/* Mobile Header */}
          <div className="sm:hidden">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {currentFolderId && (
                    <button onClick={handleBackToRoot} className="p-1 text-muted-foreground hover:text-foreground">
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <h1 className="text-2xl font-bold text-foreground truncate">
                    {currentFolderName || 'Projects'}
                  </h1>
                </div>
                <div className="flex items-center gap-2">
                  {canCreateProject && !currentFolderId && (
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-9 w-9 p-0"
                          onClick={() => { setNewFolderName(''); setShowCreateFolderModal(true); }}
                        >
                          <FolderPlus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-popover text-popover-foreground shadow-md">New Folder</TooltipContent>
                    </Tooltip>
                  )}
                  {canCreateProject && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-9 px-3"
                        >
                          New Project
                          <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={handleOpenCreateModal} className="py-3">
                          <Plus className="h-4 w-4 mr-2" />
                          Blank Project
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowImportModal(true)} className="py-3">
                          <Github className="h-4 w-4 mr-2" />
                          Import from GitHub
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowGitlabImportModal(true)} className="py-3">
                          <Gitlab className="h-4 w-4 mr-2" />
                          Import from GitLab
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              {!currentFolderId && (
                <p className="text-muted-foreground text-sm mt-1">
                  Create and manage your projects
                </p>
              )}
              {/* Mobile Search */}
              {(projectsList.length > 0 || folders.length > 0 || debouncedSearch) && (
                <div className="relative mt-3">
                  {searchLoading ? (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4">
                      <svg
                        className="h-4 w-4 text-muted-foreground animate-spin"
                        style={{ transformOrigin: 'center' }}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray="47 16"
                        />
                      </svg>
                    </div>
                  ) : (
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  )}
                  <Input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 text-sm"
                  />
                  {searchQuery.trim() && !searchLoading && (
                    <button
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden sm:block">
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0 flex-1">
                {currentFolderId ? (
                  <div className="flex items-center gap-2 mb-3 min-w-0">
                    <button
                      onClick={handleBackToRoot}
                      className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    >
                      <span className="text-2xl sm:text-3xl font-semibold tracking-tight">Projects</span>
                    </button>
                    <ChevronRight className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground truncate">
                      {currentFolderName}
                    </h1>
                  </div>
                ) : (
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-2">
                    Projects
                  </h1>
                )}
                {!currentFolderId && (
                  <p className="text-muted-foreground text-sm sm:text-base">
                    Create and manage your projects
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 sm:mt-1">
                {canCreateProject && !currentFolderId && (
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-10 w-10 p-0"
                        onClick={() => { setNewFolderName(''); setShowCreateFolderModal(true); }}
                      >
                        <FolderPlus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-popover text-popover-foreground shadow-md">New Folder</TooltipContent>
                  </Tooltip>
                )}
                {canCreateProject && (
                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      onClick={handleOpenCreateModal}
                      className="rounded-r-none h-10"
                    >
                      New Project
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-l-none border-l-0 px-2 h-10"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={handleOpenCreateModal}>
                          <Plus className="h-4 w-4 mr-2" />
                          Blank Project
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                          <Github className="h-4 w-4 mr-2" />
                          Import from GitHub
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowGitlabImportModal(true)}>
                          <Gitlab className="h-4 w-4 mr-2" />
                          Import from GitLab
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
            {/* Desktop Search - below subtitle, always show when there are projects/folders anywhere */}
            {(projectsList.length > 0 || folders.length > 0 || debouncedSearch) && (
              <div className="relative w-[300px] mt-4">
                {searchLoading ? (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4">
                    <svg
                      className="h-4 w-4 text-muted-foreground animate-spin"
                      style={{ transformOrigin: 'center' }}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="47 16"
                      />
                    </svg>
                  </div>
                ) : (
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10"
                />
                {searchQuery.trim() && !searchLoading && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Folders grid - show at root level when not searching */}
      {!currentFolderId && !debouncedSearch && folders.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">Folders</h3>
          {/* Mobile folders */}
          <div className="sm:hidden flex flex-col gap-2">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="group flex items-center gap-3 p-3 bg-card rounded-2xl border border-border hover:border-border/80 transition-all cursor-pointer"
                onClick={() => handleOpenFolder(folder)}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-purple-500/10 rounded-lg">
                  <Folder size={20} className="text-purple-500" />
                </div>
                <span className="flex-1 font-medium text-foreground truncate">{folder.name}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 text-muted-foreground hover:bg-muted/80 rounded-lg transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      setFolderToRename(folder);
                      setRenameFolderName(folder.name);
                      setShowRenameFolderModal(true);
                    }}>
                      <Pencil size={14} className="mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setFolderToDelete(folder);
                        setShowDeleteFolderModal(true);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete folder
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>

          {/* Desktop folders */}
          <div className="hidden sm:flex sm:flex-wrap gap-2">
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="group flex items-center gap-2.5 pl-3 pr-2.5 py-2.5 bg-card rounded-lg border border-border/50 hover:border-border transition-all duration-200 cursor-pointer"
                onClick={() => handleOpenFolder(folder)}
              >
                <Folder size={15} className="text-muted-foreground shrink-0" />
                <span className="font-medium text-sm text-foreground truncate max-w-[140px]">{folder.name}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1 text-muted-foreground hover:bg-muted/80 rounded-md transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical size={12} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      setFolderToRename(folder);
                      setRenameFolderName(folder.name);
                      setShowRenameFolderModal(true);
                    }}>
                      <Pencil size={14} className="mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setFolderToDelete(folder);
                        setShowDeleteFolderModal(true);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete folder
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      )}

      {projectsList.length === 0 && debouncedSearch ? (
        // No search results
        <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
          <Search size={40} className="sm:hidden mx-auto text-muted-foreground mb-3" />
          <Search size={48} className="hidden sm:block mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">No projects found</h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
            No projects match "{searchQuery.trim()}"
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-5 py-2.5 sm:px-4 sm:py-2 border border-border hover:bg-muted text-foreground rounded-xl sm:rounded-lg transition-colors text-sm"
          >
            Clear search
          </button>
        </div>
      ) : projectsList.length === 0 && !currentFolderId && folders.length === 0 ? (
        // No projects and no folders at all (empty state)
        <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
          <FolderOpen size={40} className="sm:hidden mx-auto text-muted-foreground mb-3" />
          <FolderOpen size={48} className="hidden sm:block mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
            No projects yet
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
            {canCreateProject ? 'Create your first project to get started' : 'No projects available'}
          </p>
          {canCreateProject && (
            <button
              onClick={handleOpenCreateModal}
              className="px-5 py-2.5 sm:px-4 sm:py-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-xl sm:rounded-lg transition-colors text-sm"
            >
              Create Project
            </button>
          )}
        </div>
      ) : projectsList.length === 0 && (currentFolderId || folders.length === 0) ? (
        // Empty folder, or root with no folders
        <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
          <FolderOpen size={40} className="sm:hidden mx-auto text-muted-foreground mb-3" />
          <FolderOpen size={48} className="hidden sm:block mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
            {currentFolderId ? 'This folder is empty' : 'No projects yet'}
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
            {canCreateProject
              ? (currentFolderId ? 'Move projects here or create a new one' : 'Create your first project to get started')
              : 'No projects available'}
          </p>
          {canCreateProject && (
            <button
              onClick={handleOpenCreateModal}
              className="px-5 py-2.5 sm:px-4 sm:py-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-xl sm:rounded-lg transition-colors text-sm"
            >
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className={!currentFolderId && !debouncedSearch && folders.length > 0 ? "rounded-xl bg-white dark:bg-card border border-border/50 p-4 sm:p-5" : ""}>
          {!currentFolderId && !debouncedSearch && folders.length > 0 && (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 px-1">All Projects</h3>
          )}
          {/* Mobile: Clean list view */}
          <div className="sm:hidden flex flex-col gap-3">
            {filteredProjects.map((project) => {
              const status = devContainerStatuses[project.id];
              const isProvisioning = project.mode === 'power' && status?.status && !['running', 'stopped', 'paused', 'terminated', 'error'].includes(status.status);
              const isReady = project.mode === 'power' && (status?.status === 'running' || status?.status === 'paused');
              const hasError = project.mode === 'power' && status?.status === 'error';

              return (
                <div
                  key={project.id}
                  className="group bg-card rounded-2xl border border-border hover:border-purple-500/50 transition-all duration-200 overflow-hidden relative"
                >
                  {/* More options - top right */}
                  <div className="absolute top-2 right-2 z-10">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex p-1.5 items-center justify-center text-muted-foreground hover:bg-muted/80 bg-card/80 backdrop-blur-sm rounded-lg transition-colors"
                          title="More options"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {project.latestPublishUrl && (
                          <DropdownMenuItem
                            onClick={() => {
                              navigator.clipboard.writeText(project.latestPublishUrl!);
                              toast.success('Link copied');
                            }}
                          >
                            <Copy size={14} className="mr-2" />
                            Copy link
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setProjectToMove(project);
                            setShowMoveModal(true);
                          }}
                        >
                          <FolderInput size={14} className="mr-2" />
                          Move to folder
                        </DropdownMenuItem>
                        {canDeleteProject && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(project.id, project.name)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 size={14} className="mr-2" />
                            Delete project
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                  <div className="flex gap-3 p-3 overflow-hidden">
                    {/* Thumbnail - square on left */}
                    <div className="relative w-20 h-20 flex-shrink-0 bg-muted rounded-xl overflow-hidden">
                      {project.thumbnailUrl ? (
                        <img
                          src={project.thumbnailUrl}
                          alt={`${project.name} preview`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-500/10 to-purple-600/20 ${project.thumbnailUrl ? 'hidden' : ''}`}>
                        <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                          <Code size={18} className="text-white" />
                        </div>
                      </div>
                      {/* Status indicator overlay */}
                      {project.mode === 'power' && (isProvisioning || isReady || hasError) && (
                        <div className="absolute top-0.5 left-2">
                          <span className={`inline-flex rounded-full h-2.5 w-2.5 ring-2 ring-white/50 dark:ring-white/30 ${
                            isProvisioning ? 'bg-amber-500' : isReady ? 'bg-emerald-500' : 'bg-red-500'
                          }`}></span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-6">
                      <h3 className="font-semibold text-base text-foreground truncate">
                        {project.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{formatDate(project.updatedAt)}</span>
                        {debouncedSearch && !currentFolderId && getFolderName(project.folderId) && (
                          <>
                            <span>·</span>
                            <Folder className="h-3 w-3 shrink-0" />
                            <span className="truncate max-w-[120px]">{getFolderName(project.folderId)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action button - bottom right */}
                  <div className="absolute bottom-3 right-3">
                    {project.mode === 'power' && isProvisioning ? (
                      <button
                        disabled
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-400 cursor-not-allowed text-white text-xs rounded-md font-medium"
                      >
                        <Loader2 size={12} className="animate-spin" />
                        Setting up
                      </button>
                    ) : (
                      <Link
                        to={`/dashboard/editor/${project.id}${project.mode === 'power' ? '/power' : ''}?ws=${project.workspaceId}`}
                        className="px-5 py-1.5 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white text-xs rounded-md transition-colors font-medium"
                      >
                        Open
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Grid view */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProjects.map((project) => {
              const status = devContainerStatuses[project.id];
              const isProvisioning = project.mode === 'power' && status?.status && !['running', 'stopped', 'paused', 'terminated', 'error'].includes(status.status);
              const isReady = project.mode === 'power' && (status?.status === 'running' || status?.status === 'paused');
              const hasError = project.mode === 'power' && status?.status === 'error';

              return (
                <div
                  key={project.id}
                  className="group bg-card rounded-lg border border-border hover:border-primary/30 transition-colors overflow-hidden"
                >
                  {/* Thumbnail preview or placeholder */}
                  <div className="relative aspect-[16/10] bg-muted overflow-hidden rounded-t-xl will-change-transform">
                    {project.thumbnailUrl ? (
                      <img
                        src={project.thumbnailUrl}
                        alt={`${project.name} preview`}
                        className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`absolute inset-0 flex items-center justify-center ${project.thumbnailUrl ? 'hidden' : ''}`}>
                      <Code size={24} className="text-muted-foreground/40" />
                    </div>
                    {project.mode === 'power' && (
                      <div className="absolute top-2 right-2">
                        {isProvisioning && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/70 dark:bg-black/50 backdrop-blur-sm">
                            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-amber-500"></span>
                            <span className="text-[10px] font-medium text-gray-700 dark:text-white/90">Setting up</span>
                          </div>
                        )}
                        {isReady && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/70 dark:bg-black/50 backdrop-blur-sm">
                            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                            <span className="text-[10px] font-medium text-gray-700 dark:text-white/90">Ready</span>
                          </div>
                        )}
                        {hasError && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/70 dark:bg-black/50 backdrop-blur-sm">
                            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
                            <span className="text-[10px] font-medium text-gray-700 dark:text-white/90">Error</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-3 flex flex-col min-h-[100px]">
                    <div className="mb-1">
                      <h3 className="font-semibold text-sm text-foreground truncate mb-0.5">
                        {project.name}
                      </h3>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(project.updatedAt)}
                      </span>
                      {debouncedSearch && !currentFolderId && getFolderName(project.folderId) && (
                        <div className="mt-0.5 text-xs text-muted-foreground truncate flex items-center gap-1">
                          <Folder className="h-3 w-3 shrink-0" />
                          {getFolderName(project.folderId)}
                        </div>
                      )}
                    </div>

                    <div className="h-8 mb-2">
                      {project.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {project.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 mt-auto border-t border-border/50">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="flex px-2.5 py-2 items-center justify-center border border-border text-muted-foreground hover:bg-muted rounded-md transition-colors"
                            title="More options"
                          >
                            <MoreVertical size={14} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {project.latestPublishUrl && (
                            <DropdownMenuItem
                              onClick={() => {
                                navigator.clipboard.writeText(project.latestPublishUrl!);
                                toast.success('Link copied');
                              }}
                            >
                              <Copy size={14} className="mr-2" />
                              Copy link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setProjectToMove(project);
                              setShowMoveModal(true);
                            }}
                          >
                            <FolderInput size={14} className="mr-2" />
                            Move to folder
                          </DropdownMenuItem>
                          {canDeleteProject && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(project.id, project.name)}
                              className="text-destructive focus:text-destructive"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete project
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>

                      {project.mode === 'power' && isProvisioning ? (
                        <button
                          disabled
                          className="flex items-center gap-1.5 px-5 py-1.5 bg-gray-400 cursor-not-allowed text-white text-xs rounded-md font-medium"
                        >
                          <Loader2 size={12} className="animate-spin" />
                          Provisioning
                        </button>
                      ) : project.mode === 'power' && status?.devContainerUrl ? (
                        <div className="flex items-center gap-1">
                          {enableManualPause && canEditProject && project.devContainerId && (
                            <button
                              onClick={() => handlePauseClick(project.devContainerId!)}
                              disabled={pausingContainers[project.devContainerId!] || status?.status === 'paused'}
                              className="flex items-center gap-1 px-2 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded transition-colors font-medium"
                              title={status?.status === 'paused' ? 'Container is paused' : 'Pause container'}
                            >
                              {pausingContainers[project.devContainerId!] ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Pause size={14} />
                              )}
                            </button>
                          )}
                          {project.latestPublishUrl && (
                            <a
                              href={project.latestPublishUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex p-2 items-center justify-center border border-border text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                              title="Open in new tab"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                          <Link
                            to={`/dashboard/editor/${project.id}/power?ws=${project.workspaceId}`}
                            className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-md transition-colors font-medium"
                          >
                            Open
                          </Link>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {project.latestPublishUrl && (
                            <a
                              href={project.latestPublishUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex p-2 items-center justify-center border border-border text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                              title="Open in new tab"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                          <Link
                            to={`/dashboard/editor/${project.id}${project.mode === 'power' ? '/power' : ''}?ws=${project.workspaceId}`}
                            className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-md transition-colors font-medium"
                          >
                            Open
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="project-name">Project Name *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="My Awesome Project"
                disabled={generatingName}
                className="flex-1"
                autoFocus
              />
              <Button
                type="button"
                variant="outline"
                onClick={generateProjectName}
                disabled={generatingName}
                className="h-10 px-3"
              >
                {generatingName ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!projectName.trim() || creating}
              className="flex-1"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProjectToDelete(null);
        }}
        title="Delete Project"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-foreground">
            Are you sure you want to delete <strong>"{projectToDelete?.name}"</strong>?
          </p>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone. All project files and data will be permanently deleted.
          </p>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setProjectToDelete(null);
              }}
              disabled={deleting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="flex-1"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Project'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pause Confirmation Modal */}
      <PauseContainerModal
        isOpen={showPauseModal}
        onClose={() => {
          setShowPauseModal(false);
          setPauseModalData(null);
        }}
        onConfirm={handlePauseConfirm}
        taskCount={pauseModalData?.taskCount ?? 0}
        isPausing={pauseModalData ? pausingContainers[pauseModalData.devContainerId] ?? false : false}
        mode={pauseModalData?.mode ?? 'confirm'}
      />

      {/* GitHub Import Modal */}
      <GitHubImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={(projectId) => {
          setShowImportModal(false);
          loadProjects();
          // Navigate to the imported project (Power Mode) with workspace context
          const wsId = getActiveWorkspaceId();
          navigate(`/dashboard/editor/${projectId}/power${wsId ? `?ws=${wsId}` : ''}`);
        }}
      />

      {/* GitLab Import Modal */}
      <GitLabImportModal
        isOpen={showGitlabImportModal}
        onClose={() => setShowGitlabImportModal(false)}
        onSuccess={(projectId) => {
          setShowGitlabImportModal(false);
          loadProjects();
          const wsId = getActiveWorkspaceId();
          navigate(`/dashboard/editor/${projectId}/power${wsId ? `?ws=${wsId}` : ''}`);
        }}
      />

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        title="New Folder"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Input
              id="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFolderName.trim()) {
                  handleCreateFolder();
                }
              }}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowCreateFolderModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim() || creatingFolder} className="flex-1">
              {creatingFolder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Folder'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        isOpen={showRenameFolderModal}
        onClose={() => { setShowRenameFolderModal(false); setFolderToRename(null); }}
        title="Rename Folder"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Input
              id="rename-folder-name"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameFolderName.trim()) {
                  handleRenameFolder();
                }
              }}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowRenameFolderModal(false); setFolderToRename(null); }} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleRenameFolder} disabled={!renameFolderName.trim()} className="flex-1">
              Rename
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={showDeleteFolderModal}
        onClose={() => { setShowDeleteFolderModal(false); setFolderToDelete(null); }}
        title="Delete Folder"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-foreground">
            Are you sure you want to delete the folder <strong>"{folderToDelete?.name}"</strong>?
          </p>
          <p className="text-sm text-muted-foreground">
            The folder must be empty. Move or delete all projects inside first.
          </p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowDeleteFolderModal(false); setFolderToDelete(null); }} disabled={deletingFolder} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteFolder} disabled={deletingFolder} className="flex-1">
              {deletingFolder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Folder'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Move to Folder Modal */}
      <Modal
        isOpen={showMoveModal}
        onClose={() => { setShowMoveModal(false); setProjectToMove(null); }}
        title={`Move "${projectToMove?.name || ''}"`}
        size="md"
      >
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground mb-4">Select a destination:</p>

          {/* Root option */}
          <button
            onClick={() => handleMoveProject(null)}
            disabled={movingProject || projectToMove?.folderId === null || projectToMove?.folderId === undefined}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-purple-500/50 hover:bg-muted/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FolderOpen size={18} className="text-muted-foreground shrink-0" />
            <span className="text-sm font-medium text-foreground">Root (no folder)</span>
            {(projectToMove?.folderId === null || projectToMove?.folderId === undefined) && (
              <span className="ml-auto text-xs text-muted-foreground">Current</span>
            )}
          </button>

          {/* Folder options */}
          {folders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => handleMoveProject(folder.id)}
              disabled={movingProject || projectToMove?.folderId === folder.id}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-purple-500/50 hover:bg-muted/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Folder size={18} className="text-purple-500 shrink-0" />
              <span className="text-sm font-medium text-foreground truncate">{folder.name}</span>
              {projectToMove?.folderId === folder.id && (
                <span className="ml-auto text-xs text-muted-foreground">Current</span>
              )}
            </button>
          ))}

          {folders.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No folders yet. Create a folder first.</p>
            </div>
          )}

          {movingProject && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </Modal>
      </div>
    </div>
  );
}